import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type {
  RoofRailCornerType,
  RoofPerimeterRail,
  RoofRailConerInfo,
  RoofPerimeterWallRail,
  RoofPerimeterRailType,
  RoofRailEndCondition,
} from "building-types/roofs/roofPerimeterTypes";
import { RoofPerimeterRailCondition } from "building-types/roofs/roofPerimeterTypes";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RectangularOverhangs, RoofSide } from "building-types/settings/roofTypes";
import type {
  EaveType,
  EaveCutType,
  RakeFriezeBoardMode,
  WrapFriezeBoardMode,
} from "building-types/settings/settingsTypesEnum";
import uid from "common/utils/uid";
import { rectangularOverhangsNew } from "doc/utils/roofTools";
import { DIST_EPSILON, Line2Side, ONE_MINUS_DIST_EPSILON, WindingDir } from "geom/core";
import { line2PerpDistance, line2Side } from "geom/line2";
import { offsetPolyLine2Variable } from "geom/offset";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2ByTwoPoints,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2IsAxisAlignedEdge,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { vec2Dot } from "geom/vec2";
import type { Vec3 } from "geom/vec3";

export function makeStartEndRail(
  leftRail: PolyLine2,
  rightRail: PolyLine2,
): { startRail: PolyLine2; endRail: PolyLine2 } {
  const startRail: PolyLine2 = polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]);
  const endRail: PolyLine2 = polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]);

  return { startRail, endRail };
}

export function offsetRoofPerimeter(perimeter: PolyLine2, edgeOverhangs: number[]) {
  if (!(polyLine2EdgeCount(perimeter) === edgeOverhangs.length)) {
    console.assert(false);
  }

  const sign = polyLine2WindingDir(perimeter) === WindingDir.CCW ? 1 : -1;
  const signedEdgeOverhangs = edgeOverhangs.map((overhang) => overhang * sign);

  return offsetPolyLine2Variable(perimeter, signedEdgeOverhangs);
}

export function offsetRoofPerimeterOld(
  perimeter: PolyLine2,
  startRoofOverhang: number,
  midRoofOverhang: number,
  endRoofOverhang: number,
  leftRoofOverhang: number,
  rightRoofOverhang: number,
  peakRail: PolyLine2,
) {
  const [sx, sy] = polyLine2EdgeStart(peakRail, 0);
  const [ex, ey] = polyLine2EdgeEnd(peakRail, polyLine2EdgeCount(peakRail) - 1);
  const [roofDirX, roofDirY] = polyLine2EdgeTangent(peakRail, 0);
  const sign = polyLine2WindingDir(perimeter) === WindingDir.CCW ? 1 : -1;

  startRoofOverhang *= sign;
  midRoofOverhang *= sign;
  endRoofOverhang *= sign;
  leftRoofOverhang *= sign;
  rightRoofOverhang *= sign;

  const edgeCount = polyLine2EdgeCount(perimeter);
  let minDot = Number.POSITIVE_INFINITY;
  let maxDot = Number.NEGATIVE_INFINITY;
  for (let i = 0, l = edgeCount; i < l; i++) {
    const [x0, y0] = polyLine2EdgeStart(perimeter, i);
    const dot = vec2Dot(x0 - sx, y0 - sy, roofDirX, roofDirY);
    minDot = Math.min(minDot, dot);
    maxDot = Math.max(maxDot, dot);
  }

  const offsets: number[] = new Array<number>(edgeCount).fill(0);
  for (let i = 0, l = edgeCount; i < l; i++) {
    const [x0, y0] = polyLine2EdgeStart(perimeter, i);
    const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(perimeter, i);
    const dot = Math.abs(vec2Dot(roofDirX, roofDirY, edgeDirX, edgeDirY));

    if (Math.abs(dot) > ONE_MINUS_DIST_EPSILON) {
      const side = line2Side(sx, sy, ex, ey, x0, y0);
      offsets[i] = side === Line2Side.LEFT ? leftRoofOverhang : rightRoofOverhang;
    } else if (Math.abs(dot) < DIST_EPSILON) {
      const d = vec2Dot(x0 - sx, y0 - sy, roofDirX, roofDirY);
      if (Math.abs(d - minDot) <= DIST_EPSILON) {
        offsets[i] = startRoofOverhang;
      } else if (Math.abs(d - maxDot) <= DIST_EPSILON) {
        offsets[i] = endRoofOverhang;
      } else {
        offsets[i] = midRoofOverhang;
      }
    } else {
      offsets[i] = midRoofOverhang;
    }
  }

  return offsetPolyLine2Variable(perimeter, offsets);
}

interface GetPerimeterOverhangsOptions {
  readonly perimeter: PolyLine2 | null;
  readonly midRail: PolyLine2;
  readonly leftOverhang: number;
  readonly rightOverhang: number;
  readonly startOverhang: number;
  readonly endOverhang: number;
  readonly midOverhang: number;
}

export function getPerimeterOverhangs({
  perimeter,
  midRail,
  leftOverhang,
  rightOverhang,
  startOverhang,
  endOverhang,
  midOverhang,
}: GetPerimeterOverhangsOptions): number[] {
  if (!perimeter) {
    return [];
  }

  const edgeCount = polyLine2EdgeCount(perimeter);
  const [sx, sy] = polyLine2EdgeStart(midRail, 0);
  const [ex, ey] = polyLine2EdgeEnd(midRail, 0);
  const [roofDirX, roofDirY] = polyLine2EdgeTangent(midRail, 0);

  let minDot = Number.POSITIVE_INFINITY;
  let maxDot = Number.NEGATIVE_INFINITY;
  for (let i = 0, l = edgeCount; i < l; i++) {
    const [x0, y0] = polyLine2EdgeStart(perimeter, i);
    const dot = vec2Dot(x0 - sx, y0 - sy, roofDirX, roofDirY);
    minDot = Math.min(minDot, dot);
    maxDot = Math.max(maxDot, dot);
  }

  const edgeOverhangs = new Array<number>(edgeCount).fill(0);
  for (let i = 0; i < edgeCount; i++) {
    const [x0, y0] = polyLine2EdgeStart(perimeter, i);
    const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(perimeter, i);
    const dot = Math.abs(vec2Dot(roofDirX, roofDirY, edgeDirX, edgeDirY));

    if (Math.abs(dot) > ONE_MINUS_DIST_EPSILON) {
      const side = line2Side(sx, sy, ex, ey, x0, y0);
      edgeOverhangs[i] = side === Line2Side.LEFT ? leftOverhang : rightOverhang;
    } else if (Math.abs(dot) < DIST_EPSILON) {
      const d = vec2Dot(x0 - sx, y0 - sy, roofDirX, roofDirY);
      if (Math.abs(d - minDot) <= DIST_EPSILON) {
        edgeOverhangs[i] = startOverhang;
      } else if (Math.abs(d - maxDot) <= DIST_EPSILON) {
        edgeOverhangs[i] = endOverhang;
      } else {
        edgeOverhangs[i] = midOverhang;
      }
    } else {
      edgeOverhangs[i] = midOverhang;
    }
  }

  return edgeOverhangs;
}

export function getRectangularOverhangs(
  perimeter: PolyLine2,
  offsetPerimeter: PolyLine2,
  startEdge: number,
  endEdge: number,
  exteriorWallOffsetWithSheathing: number,
): RectangularOverhangs {
  const [leftX, leftY] = polyLine2EdgeStart(perimeter, startEdge - 1);
  const [rightX, rightY] = polyLine2EdgeStart(perimeter, startEdge + 1);
  const [startX, startY] = polyLine2EdgeStart(perimeter, startEdge);
  const [endX, endY] = polyLine2EdgeStart(perimeter, endEdge);

  const leftRail = polyLine2Edge(offsetPerimeter, startEdge - 1);
  const rightRail = polyLine2Edge(offsetPerimeter, startEdge + 1);
  const startRail = polyLine2Edge(offsetPerimeter, startEdge);
  const endRail = polyLine2Edge(offsetPerimeter, endEdge);

  const startOverhang = line2PerpDistance(startRail[0], startRail[1], startRail[2], startRail[3], startX, startY);
  const endOverhang = line2PerpDistance(endRail[0], endRail[1], endRail[2], endRail[3], endX, endY);
  const leftOverhang = line2PerpDistance(leftRail[0], leftRail[1], leftRail[2], leftRail[3], leftX, leftY);
  const rightOverhang = line2PerpDistance(rightRail[0], rightRail[1], rightRail[2], rightRail[3], rightX, rightY);

  return rectangularOverhangsNew(
    startOverhang,
    endOverhang,
    leftOverhang,
    rightOverhang,
    startOverhang - exteriorWallOffsetWithSheathing,
    endOverhang - exteriorWallOffsetWithSheathing,
    leftOverhang - exteriorWallOffsetWithSheathing,
    rightOverhang - exteriorWallOffsetWithSheathing,
  );
}

export function hasDiagonalEdge(perimeter: PolyLine2): boolean {
  for (let i = 0, l = polyLine2EdgeCount(perimeter); i < l; i++) {
    if (!polyLine2IsAxisAlignedEdge(perimeter, i)) {
      return true;
    }
  }

  return false;
}
export function roofRailCornerInfoNew(
  type: RoofRailCornerType,
  neighborRail?: PolyLine3,
  miterPlane?: Plane,
  otherPerimeterRail?: RoofPerimeterRail,
  angle: number = 0,
): RoofRailConerInfo {
  return {
    type,
    neighborRail,
    miterPlane,
    otherPerimeterRail,
    angle,
  };
}

export function roofPerimeterWallRailNew(
  rail: PolyLine3,
  railWithCorners: PolyLine3,
  startCornerType: RoofRailCornerType,
  endCornerType: RoofRailCornerType,
  prevRoofPerimeterRail: RoofPerimeterRail | undefined,
  nextRoofPerimeterRail: RoofPerimeterRail | undefined,
): RoofPerimeterWallRail {
  return {
    rail,
    railWithCorners,
    startCornerType,
    endCornerType,
    prevRoofPerimeterRail,
    nextRoofPerimeterRail,
  };
}
interface RoofPerimeterRailNewOptions {
  readonly type: RoofPerimeterRailType;
  readonly isDormer: boolean;
  readonly subRoofId: string;
  readonly side: RoofSide;
  readonly edgeIndex: number;
  readonly isStartEdge: boolean;
  readonly isEndEdge: boolean;
  readonly prevPerimeterRail?: RoofPerimeterRail;
  readonly nextPerimeterRail?: RoofPerimeterRail;
  readonly startCondition?: RoofPerimeterRailCondition;
  readonly endCondition?: RoofPerimeterRailCondition;
  readonly appliedStartExtension?: number;
  readonly appliedEndExtension?: number;
  readonly edgePlane: Plane;
  readonly baseEdge: PolyLine2;
  readonly wallLimitsRail: PolyLine2;
  readonly rail: PolyLine3;
  readonly baseRail: PolyLine3;
  readonly normals: Vec3[];
  readonly baseNormals: Vec3[];
  readonly rafterDirections: Vec3[];
  readonly baseRafterDirections: Vec3[];
  readonly eaveType: EaveType;
  readonly eaveCutType: EaveCutType;
  readonly eaveReturnDistance: number;
  readonly exteriorWallOffsetWithSheathing: number;
  readonly exteriorWallOffsetWithFinish: number;
  readonly exteriorWallOffsetFromOverhangDatum: number;
  readonly overhang: number;
  readonly roofEdgeStartOverhang: number;
  readonly roofEdgeEndOverhang: number;
  readonly includeExposedRafters?: boolean;
  readonly includeFasciaBoard?: boolean;
  readonly fasciaBoardSize: number;
  readonly adjustFasciaBoardSize: boolean;
  readonly fasciaMaterial?: ExteriorMaterial;
  readonly includeSoffit?: boolean;
  readonly soffitOffset: number;
  readonly friezeBoard: FriezeBoard[] | undefined;
  readonly friezeBoardMaterialLegacy?: ExteriorMaterial;
  readonly rakeFriezeBoardExtents?: RakeFriezeBoardMode;
  readonly wrapFriezeBoardModeAtStart: WrapFriezeBoardMode;
  readonly wrapFriezeBoardModeAtEnd: WrapFriezeBoardMode;
  readonly includeFasciaTopTrim?: boolean;
  readonly fasciaTopTrimSize: number;
  readonly fasciaTopTrimMaterial?: ExteriorMaterial;
  readonly soffitMaterial?: ExteriorMaterial;
  readonly fasciaBoardThickness: number;
  readonly eaveCutTypeAtStart: RoofRailEndCondition;
  readonly eaveCutTypeAtEnd: RoofRailEndCondition;
}

export function roofPerimeterRailNew({
  type,
  isDormer,
  subRoofId,
  edgeIndex,
  side,
  isStartEdge,
  isEndEdge,
  prevPerimeterRail,
  nextPerimeterRail,
  startCondition = RoofPerimeterRailCondition.NONE,
  endCondition = RoofPerimeterRailCondition.NONE,
  appliedStartExtension = 0,
  appliedEndExtension = 0,
  edgePlane,
  baseEdge,
  wallLimitsRail,
  rail,
  baseRail,
  normals,
  baseNormals,
  rafterDirections,
  baseRafterDirections,
  eaveType,
  eaveCutType,
  eaveReturnDistance,
  exteriorWallOffsetWithSheathing,
  exteriorWallOffsetWithFinish,
  exteriorWallOffsetFromOverhangDatum,
  overhang,
  roofEdgeStartOverhang,
  roofEdgeEndOverhang,
  includeExposedRafters,
  includeFasciaBoard,
  fasciaBoardSize,
  adjustFasciaBoardSize,
  fasciaMaterial,
  includeSoffit,
  soffitOffset,
  friezeBoard,
  friezeBoardMaterialLegacy,
  rakeFriezeBoardExtents,
  wrapFriezeBoardModeAtStart,
  wrapFriezeBoardModeAtEnd,
  includeFasciaTopTrim,
  fasciaTopTrimSize,
  fasciaTopTrimMaterial,
  soffitMaterial,
  fasciaBoardThickness,
  eaveCutTypeAtStart,
  eaveCutTypeAtEnd,
}: RoofPerimeterRailNewOptions): RoofPerimeterRail {
  return {
    id: uid(),
    type,
    isDormer,
    subRoofId,
    edgeIndex,
    side,
    isStartEdge,
    isEndEdge,
    prevPerimeterRail,
    nextPerimeterRail,
    startCondition,
    endCondition,
    appliedStartExtension,
    appliedEndExtension,
    edgePlane,
    baseEdge,
    wallLimitsRail,
    rail,
    baseRail,
    normals,
    baseNormals,
    rafterDirections,
    baseRafterDirections,
    eaveType,
    eaveCutType,
    eaveReturnDistance,
    exteriorWallOffsetWithSheathing,
    exteriorWallOffsetWithFinish,
    exteriorWallOffsetFromOverhangDatum,
    overhang,
    roofEdgeStartOverhang,
    roofEdgeEndOverhang,
    includeExposedRafters,
    includeFasciaBoard,
    fasciaBoardSize,
    adjustFasciaBoardSize,
    fasciaMaterial,
    includeSoffit,
    soffitOffset,
    friezeBoard,
    friezeBoardMaterialLegacy,
    rakeFriezeBoardExtents,
    wrapFriezeBoardModeAtStart,
    wrapFriezeBoardModeAtEnd,
    includeFasciaTopTrim,
    fasciaTopTrimSize,
    fasciaTopTrimMaterial,
    soffitMaterial,
    fasciaBoardThickness,
    eaveCutTypeAtStart,
    eaveCutTypeAtEnd,
  };
}
